import { Controller } from "@hotwired/stimulus"

// A writer's aside beneath a phrase of their post: the dotted brass line the
// reader leans into. With a mouse, hovering (or focusing) floats a small card
// beneath the phrase; a click - or a tap, where there is no hover - unfolds the
// aside beneath the paragraph and leaves it there until it is closed. One
// aside is open at a time on a page.
export default class extends Controller {
  static values = { text: String }

  disconnect() {
    this.unhover()
    this.close()
  }

  // --- The hover card --------------------------------------------------------

  hover() {
    if (this.open || !window.matchMedia("(hover: hover)").matches) return

    clearTimeout(this.hoverTimer)
    this.hoverTimer = setTimeout(() => this.float(), 120)
  }

  unhover() {
    clearTimeout(this.hoverTimer)
    this.card?.remove()
    this.card = null
  }

  float() {
    if (this.card || this.open) return

    this.card = document.createElement("span")
    this.card.className = "aside-card"
    this.card.setAttribute("aria-hidden", "true")
    this.card.textContent = this.textValue
    this.element.append(this.card)

    // Kept on the page: a phrase near the right edge hangs its card to the left.
    const box = this.card.getBoundingClientRect()
    if (box.right > window.innerWidth - 12) this.card.classList.add("aside-card-left")
  }

  // --- The unfold ------------------------------------------------------------

  toggle(event) {
    event.preventDefault()
    this.open ? this.close() : this.unfold()
  }

  keydown(event) {
    if (event.key === "Enter" || event.key === " ") this.toggle(event)
    if (event.key === "Escape" && this.open) this.close()
  }

  unfold() {
    this.unhover()
    document.querySelectorAll(".aside-mark[aria-expanded='true']").forEach((mark) => {
      this.application.getControllerForElementAndIdentifier(mark, "aside")?.close()
    })

    const paragraph = this.element.closest("p") || this.element
    this.opened = document.createElement("p")
    this.opened.className = "aside-open"
    this.opened.textContent = this.textValue
    paragraph.after(this.opened)

    this.element.setAttribute("aria-expanded", "true")
    this.open = true

    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target) && !this.opened.contains(event.target)) this.close()
    }
    setTimeout(() => document.addEventListener("click", this.onDocumentClick), 0)
  }

  close() {
    if (!this.open) return

    document.removeEventListener("click", this.onDocumentClick)
    this.opened?.remove()
    this.opened = null
    this.element.setAttribute("aria-expanded", "false")
    this.open = false
  }
}
